/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import { getUserPreferences, setFavouritePreset } from './api';
import type { FavouritePresetType, UserPreferences } from './api';
import type { FavouritePreset, FavouritePresetsState } from '../types';
import {
  favouritePresetKey,
  hasNoPresetBackend,
  LIB_KEY,
  updateCachedFavourite,
} from '../utils';

export const useFavouritePresets = (
  type: FavouritePresetType,
): FavouritePresetsState => {
  const { auth, isLoggedIn } = useAuthenticationContext();
  const client = useQueryClient();
  const queryKey = [LIB_KEY, 'user-preferences', auth?.username];
  const enabled = Boolean(auth && isLoggedIn && !hasNoPresetBackend());
  const query = useQuery({
    queryKey,
    queryFn: async () => getUserPreferences(auth!),
    enabled,
    retry: false,
  });
  const mutation = useMutation({
    mutationKey: queryKey,
    scope: { id: JSON.stringify(queryKey) },
    mutationFn: async ({
      id,
      isFavourite,
    }: {
      id: string;
      isFavourite: boolean;
    }) => setFavouritePreset(type, id, isFavourite, auth!),
    onMutate: async ({ id, isFavourite }) => {
      await client.cancelQueries({ queryKey });
      const key = favouritePresetKey(type, { id });
      const previous = client.getQueryData<UserPreferences>(queryKey);
      const wasFavourite = previous?.favouritePresets?.includes(key) ?? false;
      updateCachedFavourite(client, queryKey, key, isFavourite);
      // Undo only this favourite, preserving other cached preference changes.
      return (): void =>
        updateCachedFavourite(client, queryKey, key, wasFavourite);
    },
    onError: (_error, _variables, rollback) => rollback?.(),
    onSuccess: async (preferences) => {
      await client.cancelQueries({ queryKey });
      client.setQueryData(queryKey, preferences);
    },
  });
  const isSaving = useIsMutating({ mutationKey: queryKey }) > 0;
  const isFavourite = (preset: FavouritePreset): boolean =>
    enabled &&
    Array.isArray(query.data?.favouritePresets) &&
    query.data.favouritePresets.includes(favouritePresetKey(type, preset));
  return {
    isFavourite,
    toggleFavourite: (preset: FavouritePreset): void => {
      if (enabled) {
        mutation.mutate({ id: preset.id, isFavourite: !isFavourite(preset) });
      }
    },
    disabled: !enabled || !query.isSuccess || query.isFetching || isSaving,
    isLoading: enabled && query.isFetching,
    isSaving,
    error: enabled ? query.error || mutation.error : null,
    retry: (): void => {
      mutation.reset();
      if (enabled) {
        void query.refetch();
      }
    },
  };
};
